import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { useModal } from 'context/ModalContext';
import { ModalHeader } from './ModalHeader';
import {
  ModalContent,
  MainContent,
  OptionsScrollArea,
  SummaryPanel,
} from './ModalContent';
import { ModalSection } from './ModalSection';
import { ModalSummary } from './ModalSummary';
import { ModalFooter } from './ModalFooter';

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => `${theme.colors.black}85`};
  backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`;

export const ModalContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  height: 75vh;
  max-height: 720px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 24px;
  display: grid;
  grid-template-rows: auto 1fr auto;
  transform: scale(${({ $isOpen }) => ($isOpen ? 1 : 0.95)});
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 25px 60px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  margin: 1rem;

  @media (max-width: 768px) {
    max-height: calc(85vh - env(safe-area-inset-bottom));
    margin: 0;
    border-radius: 24px 24px 0 0;
    position: fixed;
    bottom: 0;
    transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  }
`;

export const CustomizationModal = () => {
  const { isOpen, item, closeModal } = useModal();
  const [activeSection, setActiveSection] = useState(null);

  // Sort modifier groups so required ones come first
  const sortedModifierGroups = React.useMemo(() => {
    if (!item?.modifierGroups) return [];
    return [...item.modifierGroups].sort((a, b) => {
      // Required groups first
      if (a.required && !b.required) return -1;
      if (!a.required && b.required) return 1;
      // Then keep original order
      return 0;
    });
  }, [item?.modifierGroups]);

  // Auto-open first section on mount (which will now be a required one if any exist)
  useEffect(() => {
    if (isOpen && sortedModifierGroups.length > 0) {
      setActiveSection(sortedModifierGroups[0].id);
    }
  }, [isOpen, sortedModifierGroups]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeModal();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeModal]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!item) return null;

  return (
    <ModalOverlay $isOpen={isOpen} onClick={closeModal}>
      <ModalContainer $isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <ModalHeader />

        <ModalContent>
          <MainContent>
            <OptionsScrollArea>
              {sortedModifierGroups.map((group) => (
                <ModalSection
                  key={group.id}
                  group={group}
                  isOpen={activeSection === group.id}
                  onToggle={() =>
                    setActiveSection(
                      activeSection === group.id ? null : group.id,
                    )
                  }
                />
              ))}
            </OptionsScrollArea>
          </MainContent>

          <SummaryPanel>
            <ModalSummary />
          </SummaryPanel>
        </ModalContent>

        <ModalFooter />
      </ModalContainer>
    </ModalOverlay>
  );
};

export default CustomizationModal;
